/**
 * V2 Listing Adapter
 * Transforms legacy listing data shapes into V2 UI-friendly models.
 * Import and extend as needed when wiring real API responses.
 */

import type { TopListingCard } from '@/lib/home/home-public-rpc';

export interface V2ListingCard {
  id: string;
  title: string;
  rating: number;
  reviews: number;
  pricePerDay: number;
  imageUrl?: string;
  category?: string;
  location?: string;
  available?: boolean;
  /** Rental period label from home RPC (e.g. `day`) — same as main `TopListings`. */
  rentalPeriod?: string;
  ownerName?: string;
  ownerAvatarUrl?: string | null;
}

// Shape of the existing listing object (subset — extend as needed)
interface LegacyListing {
  id: string;
  title?: string;
  name?: string;
  daily_rate?: number;
  price_per_day?: number;
  images?: { url: string }[];
  photo_url?: string;
  category?: { name: string } | string;
  location?: string;
  rating?: number;
  review_count?: number;
  is_available?: boolean;
}

export function adaptListing(raw: LegacyListing): V2ListingCard {
  return {
    id:          raw.id,
    title:       raw.title ?? raw.name ?? 'Untitled',
    rating:      raw.rating ?? 4.5,
    reviews:     raw.review_count ?? 0,
    pricePerDay: raw.daily_rate ?? raw.price_per_day ?? 0,
    imageUrl:    raw.images?.[0]?.url ?? raw.photo_url,
    category:    typeof raw.category === 'string' ? raw.category : raw.category?.name,
    location:    raw.location,
    available:   raw.is_available ?? true,
  };
}

export function adaptListings(raws: LegacyListing[]): V2ListingCard[] {
  return raws.map(adaptListing);
}

/** Maps `get_top_listings_for_home` rows to V2 cards — same source as `TopListings` on the main home page. */
export function adaptTopListingCard(item: TopListingCard): V2ListingCard {
  return {
    id: item.id,
    title: item.name,
    rating: Number(item.rating ?? item.owner?.rating ?? 0),
    reviews: Number(item.reviews ?? item.owner?.reviews ?? 0),
    pricePerDay: item.price,
    imageUrl: item.image ?? undefined,
    category: item.category,
    location: item.location,
    rentalPeriod: item.period,
    ownerName: item.owner?.name,
    ownerAvatarUrl: item.owner?.avatar_url,
  };
}

// Mock data for development / storybook
export const mockListings: V2ListingCard[] = [
  { id: '1', title: 'High Pressure Washer', rating: 4.8, reviews: 32, pricePerDay: 45, category: 'Tools & DIY' },
  { id: '2', title: 'Camping Gear Set',      rating: 4.9, reviews: 18, pricePerDay: 40, category: 'Outdoor & Adventure' },
  { id: '3', title: 'Cordless Drill',         rating: 4.8, reviews: 24, pricePerDay: 25, category: 'Tools & DIY' },
  { id: '4', title: 'Extension Ladder',       rating: 4.7, reviews: 16, pricePerDay: 30, category: 'Tools & DIY' },
  { id: '5', title: 'Stand Mixer',            rating: 4.6, reviews: 11, pricePerDay: 20, category: 'Home & Garden' },
  { id: '6', title: 'Kayak',                  rating: 5.0, reviews: 8,  pricePerDay: 60, category: 'Outdoor & Adventure' },
];
